<template>
  <ak-form ref="surveyForm" class="pb-large">
    <ak-field label="Survey Name" required>
      <ak-input
        v-model="survey.name"
        type="text"
        required
        :custom-rules="slugRules"
        @input="slugify"
      />
    </ak-field>
    <ak-field ref="slugField" label="Slug" required>
      <ak-input
        ref="slugInput"
        v-model="survey.slug"
        type="text"
        required
        disabled
      />
    </ak-field>
    <ak-field label="Description">
      <ak-input v-model="survey.description" type="textarea" maxlength="1000" />
    </ak-field>
    <ak-field label="Survey Url" required>
      <ak-input v-model="survey.surveyUrl" type="url" required />
    </ak-field>
    <ak-field label="Start Date" required>
      <template #helper>Date that your survey is due to start.</template>
      <ak-date-picker
        v-model="survey.startDate"
        required
        :config="dateConfig"
      />
    </ak-field>
    <ak-field label="End Date" required>
      <template #helper>Date that your survey is due to end.</template>
      <ak-date-picker
        v-model="survey.endDate"
        required
        :config="endDateConfig"
      />
    </ak-field>
    <ak-field label="Completion Limit">
      <ak-input v-model="survey.completionLimit" type="number" min="0" />
    </ak-field>
    <ak-field label="Credits" required>
      <ak-range v-model:value="survey.credit" :min="10" :max="500" :step="10" />
    </ak-field>
    <ak-field label="Survey Access">
      <ak-select
        v-model="selectedAccess"
        :options="accessOptions"
        :allow-empty="false"
        label="label"
        :searchable="false"
        @select="selectAccess"
      />
    </ak-field>
    <ak-field label="Survey Publish Status">
      <ak-select
        v-model="selectedStatus"
        :options="statusOptions"
        :allow-empty="false"
        label="label"
        :searchable="false"
        @select="selectStatus"
      />
    </ak-field>
  </ak-form>
</template>

<script>
import {
  defineComponent,
  ref,
  watch,
  nextTick,
  onBeforeMount,
  computed,
} from 'vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import DatePicker from '@akerolabs/akero-vue/src/components/datepicker/DatePicker.vue'
import Range from '@akerolabs/akero-vue/src/components/range/Range.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import { slugger } from '@akerolabs/akero-vue/src/utils/helpers'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import moment from '@akerolabs/akero-vue/src/utils/date'

export default defineComponent({
  name: 'AkSurveyForm',
  components: {
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [DatePicker.name]: DatePicker,
    [Range.name]: Range,
    [Select.name]: Select,
  },
  props: {
    /**
     * @description The current account id
     * @type {String}
     */
    accountId: {
      type: String,
      default: undefined,
    },

    /**
     * @description The current agency id
     * @type {String}
     */

    agencyId: {
      type: String,
      default: undefined,
    },

    /**
     * @description the survey id to be used if editing
     * @type {String}
     */
    id: {
      type: String,
      default: undefined,
    },

    /**
     * @description survey name
     * @type {String}
     */
    name: {
      type: String,
      default: undefined,
    },

    /**
     * @description Survey slug
     * @type {String}
     */
    slug: {
      type: String,
      default: undefined,
    },

    /**
     * @description Survey description
     * @type {String}
     */
    description: {
      type: String,
      default: undefined,
    },

    /**
     * @description Survey survey_url
     * @type {String}
     */
    surveyUrl: {
      type: String,
      default: undefined,
    },

    /**
     * @description Survey start_date
     * @type {String|Number}
     */
    startDate: {
      type: [String, Number],
      default: undefined,
    },

    /**
     * @description Survey end_date
     * @type {String|Number}
     */
    endDate: {
      type: [String, Number],
      default: undefined,
    },

    /**
     * @description Survey completion_limit.value
     * @type {Number}
     */
    completionLimit: {
      type: Number,
      default: undefined,
    },

    /**
     * @description Survey credit.value
     * @type {Number}
     */
    credit: {
      type: Number,
      default: undefined,
    },

    /**
     * @description Survey access
     * @type {String}
     */
    access: {
      type: String,
      default: undefined,
    },

    /**
     * @description Survey status
     * @type {String}
     */
    status: {
      type: String,
      default: undefined,
    },
  },
  emits: [
    'update:name',
    'update:slug',
    'update:description',
    'update:surveyUrl',
    'update:startDate',
    'update:endDate',
    'update:completionLimit',
    'update:credit',
    'update:access',
    'update:status',
  ],
  setup(props, { emit }) {
    const surveyForm = ref(null)
    const survey = ref({
      name: props.name,
      slug: props.slug,
      description: props.description,
      surveyUrl: props.surveyUrl,
      startDate: props.startDate,
      endDate: props.endDate,
      completionLimit: props.completionLimit,
      credit: props.credit,
      access: props.access,
      status: props.status,
    })
    const slugInput = ref(null)
    const slugField = ref(null)
    const dateConfig = {
      minDate: moment(props.startDate, 'DD/MM/YYYY').startOf('day').toDate(),
      dateFormat: 'd/m/Y',
    }
    const endDateConfig = computed(() => {
      return {
        minDate: moment(survey.value.startDate, 'DD/MM/YYYY')
          .add(1, 'days')
          .startOf('day')
          .toDate(),
        dateFormat: 'd/m/Y',
      }
    })
    const accessOptions = [
      { value: 'open', label: 'Open' },
      { value: 'invite', label: 'Invite Only' },
    ]
    const statusOptions = [
      { value: 'published', label: 'Published' },
      { value: 'unpublished', label: 'Unpublished' },
    ]
    const selectedAccess = ref({ value: 'open', label: 'Open' })
    const selectedStatus = ref({ value: 'unpublished', label: 'Unpublished' })
    if (props.access) {
      selectedAccess.value = accessOptions.find(
        (option) => option.value === props.access,
      )
    }
    if (props.status) {
      selectedStatus.value = statusOptions.find(
        (option) => option.value === props.status,
      )
    }

    /**
     * @description watch functions to update internal `survey` when props update
     */
    watch(
      () => props.name,
      (value) => {
        survey.value.name = value
      },
    )
    watch(
      () => props.slug,
      (value) => {
        survey.value.slug = value
      },
    )
    watch(
      () => props.description,
      (value) => {
        survey.value.description = value
      },
    )
    watch(
      () => props.surveyUrl,
      (value) => {
        survey.value.surveyUrl = value
      },
    )
    watch(
      () => props.startDate,
      (value) => {
        survey.value.startDate = value
      },
    )
    watch(
      () => props.endDate,
      (value) => {
        survey.value.endDate = value
      },
    )
    watch(
      () => props.completionLimit,
      (value) => {
        survey.value.completionLimit = value
      },
    )
    watch(
      () => props.credit,
      (value) => {
        survey.value.credit = value
      },
    )
    watch(
      () => props.access,
      (value) => {
        survey.value.access = value
        selectedAccess.value = accessOptions.find(
          (option) => option.value === value,
        )
      },
    )
    watch(
      () => props.status,
      (value) => {
        survey.value.status = value
        selectedStatus.value = statusOptions.find(
          (option) => option.value === value,
        )
      },
    )

    /**
     * @description watch functions to emit updated values from internal `survey`
     */
    watch(
      () => survey.value.name,
      () => {
        emit('update:name', survey.value.name)
      },
    )
    watch(
      () => survey.value.slug,
      () => {
        emit('update:slug', survey.value.slug)
      },
    )
    watch(
      () => survey.value.description,
      () => {
        emit('update:description', survey.value.description)
      },
    )
    watch(
      () => survey.value.surveyUrl,
      () => {
        emit('update:surveyUrl', survey.value.surveyUrl)
      },
    )
    watch(
      () => survey.value.startDate,
      () => {
        emit('update:startDate', survey.value.startDate)
        const start = moment(survey.value.startDate, 'DD/MM/YYYY')
        const end = moment(survey.value.endDate, 'DD/MM/YYYY')
        if (end.isBefore(start)) {
          survey.value.endDate = start.add(1, 'days').format('DD/MM/YYYY')
        }
      },
    )
    watch(
      () => survey.value.endDate,
      () => {
        emit('update:endDate', survey.value.endDate)
      },
    )
    watch(
      () => survey.value.completionLimit,
      () => {
        emit('update:completionLimit', survey.value.completionLimit)
      },
    )
    watch(
      () => survey.value.credit,
      () => {
        emit('update:credit', survey.value.credit)
      },
    )
    watch(
      () => survey.value.access,
      () => {
        emit('update:access', survey.value.access)
      },
    )
    watch(
      () => survey.value.status,
      () => {
        emit('update:status', survey.value.status)
      },
    )

    /**
     * @description Validation function for survey slug
     * @param {Node} input
     */
    const uniqueSlug = async (input) => {
      let message = undefined
      let isValid = true
      const params = {
        account: props.accountId,
        agency: props.agencyId,
        slug: survey.value.slug,
      }
      if (props.id) {
        params._id = JSON.stringify({ $nin: [props.id] })
      }
      const slugCheck = await request.get('/survey', { params })
      if (slugCheck.data.data.length > 0) {
        input.setCustomValidity(' ')
        message = 'The survey slug is already in use'
        isValid = false
      } else {
        input.setCustomValidity('')
        message = ''
        isValid = true
      }
      if (survey.value.slug === '' || !survey.value.slug) {
        input.setCustomValidity('')
        message = ''
        isValid = true
      }
      slugField.value.setMessage(message)
      return isValid
    }

    const slugRules = [uniqueSlug]

    /**
     * @description convert survey.name to slug
     */
    const slugify = () => {
      nextTick(async () => {
        survey.value.slug = slugger(survey.value.name)
        slugInput.value.validate(true)
      })
    }

    /**
     * @description Select access for select
     * @param {Object}
     */
    const selectAccess = ({ value }) => {
      survey.value.access = value
    }

    /**
     * @description Select status for select
     * @param {Object}
     */
    const selectStatus = ({ value }) => {
      survey.value.status = value
    }

    /**
     * @description validate function use by parent
     */
    const validate = async () => {
      const validation = await surveyForm.value.validate(true)
      return !validation.some((v) => v === false)
    }

    onBeforeMount(() => {
      survey.value.startDate = props.startDate
      survey.value.endDate = props.endDate
    })

    return {
      surveyForm,
      survey,
      slugify,
      slugRules,
      slugInput,
      slugField,
      dateConfig,
      endDateConfig,
      accessOptions,
      statusOptions,
      selectedAccess,
      selectedStatus,
      selectAccess,
      selectStatus,
      validate,
    }
  },
})
</script>
